/* eslint-disable max-lines */
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import '@adornis/calendar/calendar.js';
import type { XCalendar } from '@adornis/calendar/calendar.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { SubscriptionPagination } from '@adornis/pagination/paginatable.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import { Product } from '@chancenwerk/shared/_cms/products/entities/Product.js';
import { ProductLKK } from '@chancenwerk/shared/_cms/products/entities/ProductLKK.js';
import { Adresse } from '@chancenwerk/shared/adresse.js';
import { filterCourseRecords } from '@chancenwerk/shared/attendance/client/filter-course-records.js';
import { generateEvents } from '@chancenwerk/shared/attendance/client/generate-events.js';
import { Course } from '@chancenwerk/shared/courses/Course.js';
import { CourseAbschluss } from '@chancenwerk/shared/courses/CourseAbschluss.js';
import { CourseDigital } from '@chancenwerk/shared/courses/CourseDigital.js';
import { COURSE_TITLES } from '@chancenwerk/shared/courses/CourseHelper.js';
import { CourseLernfoerderung } from '@chancenwerk/shared/courses/CourseLernfoerderung.js';
import { CourseLUS } from '@chancenwerk/shared/courses/CourseLUS.js';
import { GuestDayEventlog } from '@chancenwerk/shared/eventlogs/GuestDayEventlog.js';
import { CourseFilter } from '@chancenwerk/shared/filter/CourseFilter.js';
import { KpfFilter } from '@chancenwerk/shared/filter/KpfFilter.js';
import { PresenceRecord, UserPresenceRecord } from '@chancenwerk/shared/PresenceRecord.js';
import { ChancenPermission } from '@chancenwerk/shared/permissions.js';
import { StudentType } from '@chancenwerk/enums/enums.js';
import '@chancenwerk/client/design/components/cr-dropdown-selection.js';
import { CrPage } from '@chancenwerk/client/design/components/cr-page.js';
import '@chancenwerk/client/design/components/cr-progress-bar.js';
import { ChromiumSnackbar } from '@chancenwerk/client/design/components/cr-snackbar.js';
import { KPFAPI } from '@chancenwerk/shared/master-data-management/kpf/api/KPFAPI.js';
import { ProductAspect } from '@chancenwerk/shared/master-data-management/kpf/entities/aspects/ProductAspect.js';
import type { ChancenwerkUser } from '@chancenwerk/shared/master-data-management/users/entities/ChancenwerkUser.js';
import { ExternerMitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/ExternerMitarbeiter.js';
import { Student } from '@chancenwerk/shared/master-data-management/users/entities/Student.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { guard } from 'lit/directives/guard.js';
import { createRef, type Ref, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { combineLatest, merge, of, switchMap, takeUntil } from 'rxjs';
import '@chancenwerk/client/design/components/cr-nospace-toggle-content.js';
import { KPF } from '@chancenwerk/shared/master-data-management/kpf/entities/KPF.js';
import '../components/board-component-attendance-card-list.js';
import '../components/board-component-attendance-card.js';
import '../components/board-component-attendance-legend.js';
import '../components/board-component-course-digital-card.js';
import '../components/board-component-kpf-filter.js';
import { BoardDialogAddGuestLeKoCourse } from '../components/dialogs/board-dialog-add-guest-leko-course.js';
import { BoardDialogAddGuestSchuKoCourse } from '../components/dialogs/board-dialog-add-guest-schuko-course.js';
import { BoardDialogAddGuestStudentCourse } from '../components/dialogs/board-dialog-add-guest-student-course.js';
import { BoardDialogAddSubstituteLeKoCourse } from '../components/dialogs/board-dialog-add-substitute-leko-course.js';
import { BoardDialogAddSubstituteSchuKoCourse } from '../components/dialogs/board-dialog-add-substitute-schuko-course.js';

const userSelector: BaseQLSelectionSet<ChancenwerkUser | Student> = {
  _class: 1,
  _id: 1,
  vorname: 1,
  nachname: 1,
  email: 1,
  guestAccount: 1,
  handynummer: 1,
};

const courseSelector: BaseQLSelectionSet<Course | CourseAbschluss | CourseLernfoerderung> = {
  _id: 1,
  _class: 1,
  kpfID: 1,
  location: 1,
  weekday: 1,
  startTime: 1,
  endTime: 1,
  createdAt: 1,
  leKoIDs: 1,
  leKos: userSelector,
  osStudentIDs: 1,
  osStudents: userSelector,
  usStudentIDs: 1,
  usStudents: userSelector,
  schuKoIDs: 1,
  startDate: 1,
  plannedStartDate: 1,
  subject: 1,
  type: 1,
  version: 1,
  archivedAt: 1,
};

const kpfSelector: BaseQLSelectionSet<KPF<ProductLKK> | KPF> = {
  _id: 1,
  _class: 1,
  kuerzel: 1,
  school: { name: 1, kuerzel: 1, address: Adresse.allFields },
  produkt: { ...Product.allFields, useV2Courses: 1, archiveV1After: 1 },
};

const recordMinimalSelector: BaseQLSelectionSet<PresenceRecord> = {
  _id: 1,
  _class: 1,
  date: 1,
  comment: 1,
  courseID: 1,
  course: { _id: 1, _class: 1, kpfID: 1 },
};

const userRecordSelector: BaseQLSelectionSet<UserPresenceRecord> = {
  _id: 1,
  _class: 1,
  userID: 1,
  date: 1,
  status: 1,
  comment: 1,
  excuseReason: 1,
  user: userSelector,
};

const recordSelector: BaseQLSelectionSet<PresenceRecord> = {
  _id: 1,
  _class: 1,
  date: 1,
  comment: 1,
  courseID: 1,
  course: { _id: 1, _class: 1, kpfID: 1 },
  schukoRecords: { _id: 1, _class: 1, kpfID: 1, date: 1, schukoRecords: userRecordSelector },
  studentRecordsOS: userRecordSelector,
  studentRecordsUS: userRecordSelector,
  lekoRecords: userRecordSelector,
};

enum SearchType {
  ALL = 'Alle',
  SCHUKO = 'SchuKo',
  LEKO = 'LeKo',
  OS = 'Jugendliche',
  US = 'Kinder',
  GUEST = 'Gast-Schüler:in',
  GUEST_LEKO = 'Gast-LeKos',
  GUEST_SCHUKO = 'Gast-SchuKo',
}

/**
 * @customElement board-page-attendance
 */
@customElement('board-page-attendance')
export class BoardPageAttendance extends CrPage {
  @state() private readonly _size = new WindowSizeController(this);

  override styles() {
    return [...super.styles(), { ':host': { height: '100%', overflow: 'auto' } }];
  }

  constructor() {
    super();
    this.label = 'Anwesenheit';
    this.description = 'Hier kannst du die Anwesenheiten für Kurse erfassen.';
    this.stickyHeader = true;
    this.permission = ChancenPermission.board.VIEW_ATTENDANCE;
  }

  @state() private readonly _search = new RXController(this, '');
  @state() private readonly _isEventLoading = new RXController(this, false);
  @state() private readonly _showCalendar = new RXController(this, true);
  @state() private readonly _type = new RXController(this, SearchType.ALL);
  @state() private readonly _selectedKpf = new RXController<KPF | null>(this, null);
  @state() private readonly _selectedRecord = new RXController<PresenceRecord | null>(this, null);
  @state() private readonly _selectedGuestDays = new RXController(
    this,
    this._selectedKpf.observable.pipe(
      switchMap(kpf => {
        if (!kpf) {
          return of([]);
        }
        return GuestDayEventlog.subscribeGuestDayEventlogByKpf(kpf._id)({
          date: 1,
          kpfID: 1,
          user: { vorname: 1, nachname: 1, email: 1, handynummer: 1 },
        });
      }),
    ),
  );
  @state() private readonly _generatedEvents = new RXController<any[]>(this, []);

  private readonly _kpfsPaginated = new SubscriptionPagination<KPF, Array<KpfFilter>>(
    KPFAPI.subscribeByFilter.bind(KPF),
    [
      new KpfFilter({
        aspects: [
          new BooleanAspect({ customID: 'active', value: true }),
          new ProductAspect({ values: [ProductLKK._class] }),
        ],
      }),
    ],
    kpfSelector,
    15,
  );

  @state() private readonly _kpfs = new RXController(this, this._kpfsPaginated.data);
  @state() private readonly _extraCourses = new RXController(this, [] as Course[]);
  @state() private readonly _coursesFromKPF = new RXController(
    this,
    combineLatest([this._selectedKpf.observable, this._extraCourses.observable]).pipe(
      switchMap(([kpf, extraCourses]) => {
        const courses = kpf ? Course.subscribeAllCoursesIncludingArchivedByKPFID(kpf._id)(courseSelector) : of([]);
        return merge(courses, of(extraCourses));
      }),
    ),
  );
  private readonly _recordsFromCourses = new RXController<Record<string, PresenceRecord[]>>(this, {});

  calRef: Ref<XCalendar> = createRef();

  private async _recordRefetch(courses: Course[]) {
    const recordPromises = courses.map(c =>
      PresenceRecord.getPresenceRecordForCourse(
        c._id,
        Date.now(),
      )(recordMinimalSelector).then(records => [c._id, records ?? []] as const),
    );
    const records = Object.fromEntries(await Promise.all(recordPromises));
    this._recordsFromCourses.next(records);
    this._generatedEvents.next(this._generateEvents());
    this.calRef.value?.requestUpdate();
  }

  override connectedCallback(): void {
    super.connectedCallback();

    this._kpfsPaginated.loadingInitially.pipe(takeUntil(this.disconnected)).subscribe(() => {
      this.requestUpdate();
    });

    this._kpfsPaginated.updateParameters(
      new KpfFilter({
        aspects: [
          new BooleanAspect({ customID: 'active', value: true }),
          new ProductAspect({ values: [ProductLKK._class] }),
        ],
      }),
    );

    combineLatest([this._coursesFromKPF.observable, this._selectedGuestDays.observable])
      .pipe(takeUntil(this.disconnected))
      .subscribe(async values => {
        const [courses] = values;
        if (!courses) {
          this._recordsFromCourses.next({});
          return;
        }
        await this._recordRefetch(courses);
      });

    // * Query param parsing to make it possible to open a certain course via the week overview
    // * Parameters: kpf=KPF_ID, course=COURSE_ID, date=DATE_TO_OPEN
    if (location.search.length > 0) {
      const queryParams = new URLSearchParams(location.search.substring(1));
      if (queryParams.has('kpf')) {
        KPF.getByID<KPF>(queryParams.get('kpf') ?? '')(kpfSelector)
          .then(kpf => {
            this._selectedKpf.next(kpf ?? null);
            this._extraCourses.next([]);
          })
          .catch(console.error);

        if (queryParams.has('course') && queryParams.has('date')) {
          const date = DateTime.fromISO(queryParams.get('date') ?? '');

          this._showCalendar.next(false);
          PresenceRecord.getPresenceRecordForCourseAndDate(
            queryParams.get('course') ?? '',
            date,
          )(recordSelector)
            .then(record => {
              this._selectedRecord.next(record);
            })
            .catch(console.error);
        }
      }

      // ? Needs to be executed a little delayed or otherwise has no effect
      setTimeout(() => history.replaceState(null, '', '?'), 100);
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();

    this._kpfsPaginated.stop();
  }

  private async _createGuestStudent() {
    const newId: string = await BoardDialogAddGuestStudentCourse.showPopup({
      props: {
        // ? Do we want the guest to be assigned to all courses?
        // * If we want to do that:
        // courses: this._coursesFromKPF.value,
        courses: [await Course.getByID<Course>(this._selectedRecord.value?.courseID ?? '')(Course.allFields)],
        kpfID: this._selectedKpf.value?._id,
      },
    });

    if (newId) {
      const student = await Student.getByID<Student>(newId)({
        _id: 1,
        _class: 1,
        schuelertyp: 1,
        vorname: 1,
        nachname: 1,
      });

      if (!student) {
        return;
      }

      switch (student.schuelertyp) {
        case StudentType.OS:
          this._selectedRecord.value?.studentRecordsOS?.push(new UserPresenceRecord({ userID: newId, user: student }));
          break;
        case StudentType.US:
          this._selectedRecord.value?.studentRecordsUS?.push(new UserPresenceRecord({ userID: newId, user: student }));
          break;
      }

      void ChromiumSnackbar.show('Gastschüler erstellt');

      this.requestUpdate();
    }
  }

  private async _createGuestLeKo() {
    const newId: string = await BoardDialogAddGuestLeKoCourse.showPopup({
      props: {
        courses: [await Course.getByID<Course>(this._selectedRecord.value?.courseID ?? '')(Course.allFields)],
        kpfID: this._selectedKpf.value?._id,
      },
    });

    if (newId) {
      const leko = await ExternerMitarbeiter.getByID<ExternerMitarbeiter>(newId)({
        _id: 1,
        _class: 1,
        vorname: 1,
        nachname: 1,
        type: 1,
      });

      if (!leko) {
        return;
      }

      this._selectedRecord.value?.lekoRecords?.push(new UserPresenceRecord({ userID: newId, user: leko }));
      void ChromiumSnackbar.show('Gast-LeKo erstellt');
      this.requestUpdate();
    }
  }

  private async _createGuestSchuKo() {
    const newId: string = await BoardDialogAddGuestSchuKoCourse.showPopup({
      props: {
        courses: await Course.getByID<Course>((this._coursesFromKPF.value ?? []).map(c => c._id))(Course.allFields),
        kpfID: this._selectedKpf.value?._id,
      },
    });

    if (newId) {
      const schuko = await ExternerMitarbeiter.getByID<ExternerMitarbeiter>(newId)({
        _id: 1,
        _class: 1,
        vorname: 1,
        nachname: 1,
        type: 1,
      });

      if (!schuko) {
        return;
      }

      this._selectedRecord.value?.schukoRecords?.schukoRecords?.push(
        new UserPresenceRecord({ userID: newId, user: schuko }),
      );
      void ChromiumSnackbar.show('Gast-SchuKo erstellt');
      this.requestUpdate();
    }
  }

  private async _addLeKoSubstitute() {
    await BoardDialogAddSubstituteLeKoCourse.showPopup({
      props: { record: this._selectedRecord.value, kpfID: this._selectedKpf.value?._id },
    });
    this._selectedRecord.next(this._selectedRecord.value);
    this.requestUpdate();
  }

  private async _addSchuKoSubstitute() {
    await BoardDialogAddSubstituteSchuKoCourse.showPopup({
      props: { record: this._selectedRecord.value, kpfID: this._selectedKpf.value?._id },
    });
    this._selectedRecord.next(this._selectedRecord.value);
    this.requestUpdate();
  }

  private async _saveSchuKoRecord() {
    if (this._selectedRecord.value?.schukoRecords) {
      if (!this._selectedRecord.value.schukoRecords._id) {
        await this._selectedRecord.value.schukoRecords.create();
      } else {
        await this._selectedRecord.value.schukoRecords.save();
      }
    }
  }

  private _recordDirty() {
    return this._selectedRecord.value?.isDirty(true);
  }

  private async _saveRecord() {
    await ChromiumDialog.guard<void>(async () => {
      await this._saveSchuKoRecord();
      if (!this._selectedRecord.value?._id) {
        await this._selectedRecord.value?.create();
        this._selectedRecord.next(this._selectedRecord.value);

        // * re-fetch the changed
        this._recordsFromCourses.value[this._selectedRecord.value?.courseID ?? ''] =
          (await PresenceRecord.getPresenceRecordForCourse(
            this._selectedRecord.value?.courseID ?? '',
            Date.now(),
          )(recordMinimalSelector)) ?? [];

        this._recordsFromCourses.next(this._recordsFromCourses.value);
        this._generatedEvents.next(this._generateEvents());
        this.calRef.value?.requestUpdate();
      } else {
        await this._selectedRecord.value.save();
      }
    });

    this.requestUpdate();
  }

  override header() {
    return html`
      <cr-flex horizontal space="md" crossaxis-center>
        ${this._selectedRecord.value
          ? html`<cr-button icon="save" slot="right" @click=${this._saveRecord}>Speichern</cr-button>`
          : nothing}
      </cr-flex>
    `;
  }

  override content() {
    return html`
      <cr-flex space="sm" padding="xs" center>
        <cr-flex space="sm" crossaxis-center> ${this._kpfCourseSelect()} </cr-flex>
        ${this._attendanceList()}
      </cr-flex>
    `;
  }

  private _hiddenDays() {
    const hidden = [1, 2, 3, 4, 5].filter(d => {
      return !this._generatedEvents.value.some(e => e.day === d);
    });

    if (hidden.length === 5) {
      return [];
    }

    return hidden;
  }

  private _LUSSelected() {
    return this._coursesFromKPF.value?.at(0)?._class === CourseLUS._class;
  }

  private _generateEvents() {
    return generateEvents(
      this,
      this._coursesFromKPF.value ?? [],
      this._recordsFromCourses.value,
      this._selectedGuestDays.value,
      this._selectedKpf.value?.produkt,
    );
  }

  private async _calendarEventClick(e: CustomEvent<any>) {
    if (this._recordDirty()) {
      const confirm = await ChromiumDialog.confirm(
        'Du hast noch ungespeicherte Änderungen. Willst du wirklich fortfahren?',
      );
      if (!confirm) {
        return;
      }
    }

    this._isEventLoading.next(true);
    const ids: string[] = e.detail.event.id.split('-');
    this._selectedRecord.next(
      await PresenceRecord.getPresenceRecordForCourseAndDate(
        ids[0] ?? '',
        DateTime.fromISO(e.detail.event.startStr),
      )(recordSelector),
    );
    this._showCalendar.next(false);
    this._isEventLoading.next(false);
  }

  private _kpfCourseSelect() {
    const kpfSelect = html` <board-component-kpf-filter
      ${css({ maxWidth: '500px', width: '100%' })}
      .kpfsPaginated=${this._kpfsPaginated}
    >
      <chancen-component-card-title slot="front">Kursauswahl</chancen-component-card-title>
      ${this._kpfsPaginated.loadingInitially.value
        ? html`<cr-flex crossaxis-center><cr-loader ${css({ width: '25px', height: '25px' })}></cr-loader></cr-flex>`
        : html`<cr-dropdown-selection
              clearable
              .value=${this._selectedKpf.value}
              ?flex=${!this._selectedKpf.value}
              icon="search"
              placeholder=${this._kpfsPaginated.data.value?.length === 0
                ? 'KPF'
                : `KPF (${this._kpfsPaginated.data.value?.length} gefunden)`}
              .selectables=${this._kpfs.value}
              .renderString=${(entry: Maybe<KPF>) => {
                if (!entry) return 'KPF auswählen';
                return entry.stringRepresentation;
              }}
              @value-picked=${(e: CustomEvent<{ value: KPF }>) => {
                this._selectedKpf.next(e.detail.value);
                this._extraCourses.next([]);
                this._selectedRecord.next(null);
              }}
            ></cr-dropdown-selection>

            ${this._digitialCourseSelect()}`}
    </board-component-kpf-filter>`;

    let calendar = html`<cr-flex w100 padding="lg" space="md" crossaxis-center>
      <cr-icon ${css({ fontSize: '2rem' })}>today</cr-icon>
      <cr-text>Bitte wähle einen Kurs aus</cr-text>
    </cr-flex>`;
    if (
      this._selectedKpf.value &&
      (this._recordsFromCourses.isLoading || this._coursesFromKPF.isLoading || this._generatedEvents.value.length === 0)
    ) {
      if (!this._coursesFromKPF.isLoading && this._coursesFromKPF.value?.length === 0) {
        calendar = html`<cr-flex w100 padding="lg" space="md" crossaxis-center>
          <cr-icon ${css({ fontSize: '2rem' })}>warning</cr-icon>
          <cr-text>Dieses KPF hat keine Kurse!</cr-text>
        </cr-flex>`;
      } else {
        calendar = html`<cr-flex w100 padding="lg" space="md" crossaxis-center>
          <cr-loader></cr-loader>
          <cr-text>Lade Kalender...</cr-text>
        </cr-flex>`;
      }
    } else if ((this._coursesFromKPF.value?.length ?? 0) > 0) {
      calendar = html` <cr-nospace-toggle-content .label=${'Kalender'} .open=${this._showCalendar.value}>
        ${guard(
          [this._selectedKpf.value, this._showCalendar.value, this._generatedEvents.value],
          () =>
            html`<x-calendar
              ${ref(this.calRef)}
              week
              month
              day
              .events=${this._generatedEvents.value}
              .options=${{ weekends: false, hiddenDays: this._hiddenDays(), initialView: 'listDay' }}
              @eventClick=${(e: CustomEvent<any>) => this._calendarEventClick(e)}
            >
            </x-calendar>`,
        )}
      </cr-nospace-toggle-content>`;
    }

    if ((this.calRef.value as any)?._calendar) {
      (this.calRef.value as any)._calendar.setOption('hiddenDays', this._hiddenDays());
    }

    return html`${kpfSelect} <cr-card w100>${calendar}</cr-card>`;
  }

  private _digitialCourseSelect() {
    return permissionGuard(
      ChancenPermission.board.VIEW_ATTENDANCE_DIGITAL,
      () =>
        html`<cr-flex space="md">
          <cr-text ${css({ textAlign: 'center', opacity: 0.7 })}>oder</cr-text>
          <x-entity-picker
            flex
            placeholder="Digitale Intensivkurse"
            clearable
            .value=${this._extraCourses.value[0]?._id}
            .options=${{
              class: CourseDigital,
              operation: (skip, limit, filter) => {
                return CourseDigital.subscribeCoursesByFilter(filter);
              },
              selectionSet: {
                name: 1,
                subject: 1,
                jahrgangsstufe: 1,
                weekday: 1,
                startTime: 1,
                endTime: 1,
                ...courseSelector,
              },
              filter: new CourseFilter({ searchFields: ['weekday', 'subject'], courseType: CourseDigital._class }),
            }}
            .renderString=${(e: Maybe<CourseDigital>) => {
              if (!e) {
                return '';
              }
              return `${e?.subject} ${e?.jahrgangsstufe} ${e?.weekday} (${e?.startTime?.toFormat(
                'HH:mm',
              )} - ${e?.endTime?.toFormat('HH:mm')})`;
            }}
            @entity-picked=${(e: ValueEvent<Maybe<CourseDigital>>) => {
              this._selectedKpf.next(null);
              this._selectedRecord.next(null);
              if (!e.detail.value) {
                this._extraCourses.next([]);
                return;
              }

              this._extraCourses.next([e.detail.value]);
            }}
          ></x-entity-picker>
        </cr-flex>`,
    );
  }

  private _guestDay() {
    const guestDays = this._selectedGuestDays.value?.filter(
      (g: GuestDayEventlog) =>
        g.date?.toFormat('yyyy-MM-dd') === this._selectedRecord.value?.date.toFormat('yyyy-MM-dd'),
    );

    if (!guestDays?.length) {
      return html`<cr-text>Keine Gäste</cr-text>`;
    }

    return html`${repeat(
      guestDays,
      d => d._id,
      d => {
        return html`<cr-card>
          <cr-flex space="md" horizontal space-between>
            <cr-text bold>${d.user?.vorname} ${d.user?.nachname}</cr-text>
            <cr-flex horizontal space="md">
              ${d.user?.handynummer ? html`<cr-text>Handynummer: ${d.user?.handynummer}</cr-text>` : nothing}
              ${d.user?.email ? html`<cr-text>E-Mail: ${d.user?.email}</cr-text>` : nothing}
            </cr-flex>
          </cr-flex>
        </cr-card>`;
      },
    )}`;
  }

  private _filterCourseTemplate() {
    if (!this._selectedRecord.value) {
      return nothing;
    }

    return html`<cr-nospace-toggle-content label="Filter">
      <cr-flex space="sm">
        <cr-input
          placeholder="Name"
          .value=${this._search.value}
          @value-changed=${(e: ValueEvent<string>) => this._search.next(e.detail.value)}
        ></cr-input>
        <cr-dropdown-selection
          placeholder="Typ"
          .value=${this._type.value}
          .selectables=${Object.values(SearchType)}
          @value-picked=${(e: ValueEvent<SearchType>) => this._type.next(e.detail.value)}
        ></cr-dropdown-selection>
      </cr-flex>
    </cr-nospace-toggle-content>`;
  }

  private _attendanceListSingle(type: string, records: UserPresenceRecord[] | undefined, guestUsers: boolean) {
    if ((type === SearchType.SCHUKO || type === SearchType.OS) && this._LUSSelected()) {
      return nothing;
    }

    if (this._type.value !== SearchType.ALL && this._type.value !== type) {
      return nothing;
    }

    const filteredRecords = filterCourseRecords(this._search.value, records, guestUsers);
    if (filteredRecords.length === 0) {
      return nothing;
    }

    const titleStyle = { margin: `${this.spacing.md} 0 ${this.spacing.sm} 0`, fontWeight: '600 !important' };
    return html`<cr-h3 ${css(titleStyle)}>${type}</cr-h3>
      <board-component-attendance-card-list
        .records=${filteredRecords}
        empty-name=${type}
        @status-click=${() => this.requestUpdate()}
        @user-deleted=${async e => {
          await this._saveRecord();

          // * We re-fetch the presence record so that the resolver will re-fetch the users
          // * and remove the now deleted user.
          const updated = await PresenceRecord.getPresenceRecordForCourseAndDate(
            this._selectedRecord.value?.courseID ?? '',
            this._selectedRecord.value?.date ?? DateTime.now(),
          )(recordSelector);

          this._selectedRecord.next(updated);
        }}
      ></board-component-attendance-card-list>`;
  }

  private _attendanceList() {
    if (this._isEventLoading.value) {
      return html`<x-loader></x-loader>`;
    }

    if (!this._selectedRecord.value) {
      return nothing;
    }

    const titleStyle = { fontWeight: '600 !important' };
    const course = this._coursesFromKPF.value?.find(c => c._id === this._selectedRecord.value?.courseID);
    return html`
      ${this._recordDirty()
        ? html`<cr-flex
            ${css({ zIndex: '10000', position: 'absolute', top: '60px', left: '0', width: '100%' })}
            padding="md"
          >
            <cr-card
              ${css({ width: '100%', backgroundColor: this.colors.tone.error.shade(100) })}
              padding="md"
              space="md"
            >
              <cr-h3>Achtung!</cr-h3>
              <cr-text> Ungespeicherte Änderungen! Bitte nicht vergessen zu speichern. </cr-text>
            </cr-card>
          </cr-flex>`
        : nothing}

      <cr-grid columns=${this._size.mobile ? '1fr' : '1fr 1fr 1fr'} space="sm">
        <cr-card>
          <cr-flex
            space="md"
            padding="sm"
            ${css({
              paddingBottom: this.spacing.md,
              marginBottom: this.spacing.md,
              borderBottom: '1px solid rgba(0,0,0,0.1)',
            })}
          >
            <cr-h2 ${css(titleStyle)}>${this._selectedRecord.value.date.toFormat('dd. LLL yyyy')}</cr-h2>
            ${course?._class != CourseAbschluss._class
              ? html`<cr-h3> ${COURSE_TITLES[course?._class ?? '']} in Raum ${course?.location ?? '???'} </cr-h3>`
              : html`<cr-h3> ${COURSE_TITLES[course?._class ?? '']} </cr-h3>`}
          </cr-flex>

          <cr-input
            placeholder="Kommentar für den Tag"
            .value=${this._selectedRecord.value.comment}
            @value-changed=${(e: ValueEvent<string>) => {
              if (this._selectedRecord.value) {
                this._selectedRecord.value.comment = e.detail.value;
                this._selectedRecord.next(this._selectedRecord.value);
              }
            }}
          ></cr-input>
        </cr-card>

        <cr-card space="sm">
          <chancen-component-card-title>Personen-Filter</chancen-component-card-title>
          ${this._filterCourseTemplate()}
        </cr-card>

        <cr-card space="sm">
          <chancen-component-card-title>Schnupper-Gäste</chancen-component-card-title>
          ${this._guestDay()}
        </cr-card>
      </cr-grid>

      <cr-h3 ${css({ ...titleStyle, marginTop: this.spacing.md })}>Legende</cr-h3>
      <board-component-attendance-legend></board-component-attendance-legend>

      ${this._attendanceListUsers()}

      <cr-flex
        ?horizontal=${!this._size.mobile}
        ?wrap=${!this._size.mobile}
        space=${this._size.mobile ? 'md' : 'sm'}
        ${css({ marginTop: this.spacing.md, display: this._selectedKpf.value ? undefined : 'none' })}
      >
        <cr-button
          @click=${this._createGuestStudent}
          ${css({ maxWidth: this._size.mobile ? '100%' : '350px', display: this._LUSSelected() ? 'none' : undefined })}
        >
          Gastschüler Hinzufügen
        </cr-button>
        <cr-button @click=${this._createGuestLeKo} ${css({ maxWidth: this._size.mobile ? '100%' : '350px' })}>
          Gast-LeKo Hinzufügen
        </cr-button>
        <cr-button
          @click=${this._createGuestSchuKo}
          ${css({ maxWidth: this._size.mobile ? '100%' : '350px', display: this._LUSSelected() ? 'none' : undefined })}
        >
          Gast-SchuKo Hinzufügen
        </cr-button>
        <cr-button @click=${this._addLeKoSubstitute} ${css({ maxWidth: this._size.mobile ? '100%' : '350px' })}>
          LeKo-Vertretung Hinzufügen
        </cr-button>
        <cr-button @click=${this._addSchuKoSubstitute} ${css({ maxWidth: this._size.mobile ? '100%' : '350px' })}>
          SchuKo-Vertretung Hinzufügen
        </cr-button>
      </cr-flex>
    `;
  }

  private _attendanceListUsers() {
    if (!this._selectedRecord.value) {
      return nothing;
    }

    return html` ${this._attendanceListSingle(
      SearchType.SCHUKO,
      this._selectedRecord.value.schukoRecordsFiltered ?? [],
      false,
    )}
    ${this._attendanceListSingle(SearchType.GUEST_SCHUKO, this._selectedRecord.value.schukoRecordsFiltered ?? [], true)}
    ${this._attendanceListSingle(SearchType.LEKO, this._selectedRecord.value.lekoRecords, false)}
    ${this._attendanceListSingle(SearchType.GUEST_LEKO, this._selectedRecord.value.lekoRecords, true)}
    ${this._attendanceListSingle(SearchType.US, this._selectedRecord.value.studentRecordsUS, false)}
    ${this._attendanceListSingle(SearchType.OS, this._selectedRecord.value.studentRecordsOS, false)}
    ${this._attendanceListSingle(
      SearchType.GUEST,
      [
        ...(this._selectedRecord.value.course?._class === CourseLernfoerderung._class
          ? (this._selectedRecord.value.studentRecordsUS ?? [])
          : []),
        ...(this._selectedRecord.value.studentRecordsOS ?? []),
      ],
      true,
    )}`;
  }
}
